import { useEffect, useState } from 'react';
import ActionStatusText from '../../components/ui/ActionStatusText';
import ErrorMessage from '../../components/ui/ErrorMessage';
import ModalHeader from '../components/ModalHeader';
import useCreateFavoriteSession from '../../hooks/data/exercise-sessions/favorite/useCreateFavoriteSession';
import useCreateFavoriteMeal from '../../hooks/data/meals/useCreateFavoriteMeal';
import { useModalEntry } from '../../context/ModalEntryContext';
import { useModalEntryType, useModalNavigation } from '../../context/ModalControllerContext';

const ModalAddFavorite = ({ currentDay }) => {
    const { selectedEntryId, selectedEntry } = useModalEntry();
    const { close, closeWithDelay } = useModalNavigation();

    const { entryType } = useModalEntryType();
    const isExercise = entryType === 'exercise';

    // MEAL FAVORITE
    const { createFavoriteMeal, isAdding, isSuccess, isError, error } = useCreateFavoriteMeal(currentDay);

    // EXERCISE FAVORITE
    const {
        mutateAsync: createExerciseFavorite,
        isPending: isCreatingExerciseFavorite,
        isSuccess: isExerciseSuccess,
        isError: isExerciseError,
        error: exerciseError
    } = useCreateFavoriteSession();

    const [inputValue, setInputValue] = useState(selectedEntry?.name ?? '');

    const isLocked = isExercise ? isCreatingExerciseFavorite || isExerciseSuccess : isAdding || isSuccess;

    const originalName = (selectedEntry?.name ?? '').trim();
    const trimmedInput = inputValue.trim();
    const hasChanged = trimmedInput !== originalName;

    const handleSubmit = async () => {
        if (!trimmedInput || !hasChanged) return;

        if (isExercise) {
            await createExerciseFavorite({
                sessionId: selectedEntryId,
                name: trimmedInput
            });

            closeWithDelay();
            return;
        }

        await createFavoriteMeal({
            mealId: selectedEntryId,
            name: trimmedInput
        });

        closeWithDelay();
    };

    useEffect(() => {
        if (selectedEntry?.name) setInputValue(selectedEntry.name);
    }, [selectedEntry?.name]);

    if (isError || isExerciseError) {
        return (
            <ErrorMessage
                message={
                    error?.message || exerciseError?.message || 'Ett fel uppstod vid skapandet av favorit.'
                }
            />
        );
    }

    return (
        <>
            <ModalHeader
                title={isExercise ? 'Skapa favoritträningspass' : 'Skapa favoritmåltid'}
                onClose={close}
                showBack={false}
                disableButtons={isLocked}
            />

            <div className="modal-body add-favorite">
                <div className="input-with-label">
                    <span className="inline-label">Namn:</span>
                    <input
                        type="text"
                        className="modal-input name-input"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        disabled={isLocked}
                        placeholder={
                            isExercise ? 'Ange namn för träningspass' : 'Ange namn för favoritmåltid'
                        }
                    />
                </div>
            </div>

            <div className="modal-footer add-favorite">
                <button
                    className="btn-green"
                    onClick={handleSubmit}
                    disabled={isLocked || !trimmedInput || !hasChanged}
                >
                    <ActionStatusText
                        isProcessing={isExercise ? isCreatingExerciseFavorite : isAdding}
                        isCompleted={isExercise ? isExerciseSuccess : isSuccess}
                        processingLabel="Sparar"
                        successLabel="Favorit sparad!"
                        defaultLabel="Spara"
                    />
                </button>

                <button onClick={close} className="marked-border-btn" disabled={isLocked}>
                    Avbryt
                </button>
            </div>
        </>
    );
};

export default ModalAddFavorite;
